/**
 * Objects returned by the public REST API v1 (FR-API-01). The server builds its responses to
 * these types and the OpenAPI document is generated from them (FR-API-08), so the docs, the SDK
 * and the server cannot drift apart. Money is integer poisha; quantities are decimals in the
 * item's unit; timestamps are Unix epoch milliseconds.
 */
import { z } from "zod";

const money = z.number().int().describe("Amount in poisha (1/100 taka)");
const quantity = z.number().describe("Quantity in the unit shown, up to three decimals");
const timestamp = z.number().int().describe("Unix epoch milliseconds");
const bilingual = z.object({ en: z.string(), bn: z.string() });

export const itemObject = z.object({
  object: z.literal("item"),
  id: z.string(),
  name: bilingual,
  categoryId: z.string().nullable(),
  brand: z.string().nullable(),
  unit: z.string().describe("Base unit code, e.g. pc or kg"),
  vatRate: z.number().int().describe("Basis points: 1500 = 15%"),
  sdRate: z.number().int().describe("Supplementary duty, basis points"),
  trackStock: z.boolean(),
  status: z.enum(["active", "archived"]),
  description: bilingual,
  images: z.array(z.url()).describe("Photo URLs, main photo first"),
  custom: z.record(z.string(), z.unknown()).describe("Business-type fields, e.g. generic name"),
  units: z
    .array(
      z.object({
        code: z.string(),
        factor: quantity.describe("Base units in one of this unit"),
        price: money.nullable().describe("Fixed price for the whole unit, if set"),
      }),
    )
    .describe("Pack sizes the item is sold in besides its base unit"),
  variants: z.array(
    z.object({
      id: z.string(),
      sku: z.string(),
      attributes: z.record(z.string(), z.string()),
      price: money.describe("Price per base unit, poisha"),
      mrp: money.nullable(),
      barcodes: z.array(z.string()),
      available: quantity
        .nullable()
        .describe("Sellable now at the requested branch; null when stock is not tracked"),
    }),
  ),
  createdAt: timestamp,
  updatedAt: timestamp,
});

export const categoryObject = z.object({
  object: z.literal("category"),
  id: z.string(),
  parentId: z.string().nullable(),
  name: bilingual,
  position: z.number().int(),
});

export const branchObject = z.object({
  object: z.literal("branch"),
  id: z.string(),
  code: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  address: z.string().nullable(),
  isMain: z.boolean(),
});

export const stockLevelObject = z.object({
  object: z.literal("stock_level"),
  variantId: z.string(),
  branchId: z.string(),
  trackStock: z.boolean(),
  onHand: quantity,
  reserved: quantity.describe("Held for open orders"),
  available: quantity.nullable(),
});

export const customerObject = z.object({
  object: z.literal("customer"),
  id: z.string(),
  name: z.string(),
  phone: z.string().nullable().describe("E.164, e.g. +8801712345678"),
  address: z.string().nullable(),
  balance: money.describe("What the customer owes the business"),
  createdAt: timestamp,
});

export const orderObject = z.object({
  object: z.literal("order"),
  id: z.string(),
  number: z.string(),
  status: z.enum([
    "pending",
    "confirmed",
    "packed",
    "shipped",
    "delivered",
    "cancelled",
    "returned",
  ]),
  channel: z.enum(["api", "storefront", "manual"]),
  branchId: z.string(),
  customer: z.object({ id: z.string(), name: z.string(), phone: z.string() }),
  shipping: z.object({
    address: z.string(),
    districtId: z.string(),
    district: bilingual.nullable(),
    area: z.string().nullable(),
  }),
  lines: z.array(
    z.object({
      id: z.string(),
      variantId: z.string(),
      name: z.string(),
      unit: z.string(),
      quantity,
      unitPrice: money,
      vatRate: z.number().int(),
      vat: money,
      sd: money,
      total: money,
    }),
  ),
  subtotal: money.describe("Value before VAT and SD"),
  vat: money,
  sd: money,
  deliveryCharge: money,
  total: money,
  payment: z.object({
    method: z.enum(["cod", "prepaid"]),
    reference: z.string().nullable(),
    paid: money,
    status: z.enum(["unpaid", "partially_paid", "paid"]),
  }),
  note: z.string().nullable(),
  externalRef: z.string().nullable(),
  saleId: z.string().nullable().describe("The sale recorded when the order shipped"),
  cancelReason: z.string().nullable(),
  placedAt: timestamp,
  confirmedAt: timestamp.nullable(),
  packedAt: timestamp.nullable(),
  shippedAt: timestamp.nullable(),
  deliveredAt: timestamp.nullable(),
  cancelledAt: timestamp.nullable(),
  returnedAt: timestamp.nullable(),
});

export const saleObject = z.object({
  object: z.literal("sale"),
  id: z.string(),
  number: z.string(),
  status: z.enum(["completed", "void"]),
  branchId: z.string(),
  customerId: z.string().nullable(),
  soldAt: timestamp,
  lines: z.array(
    z.object({
      id: z.string(),
      variantId: z.string(),
      name: z.string(),
      unit: z.string(),
      quantity,
      returnedQuantity: quantity,
      unitPrice: money,
      discount: money,
      vat: money,
      sd: money,
      total: money,
    }),
  ),
  subtotal: money,
  discount: money,
  vat: money,
  sd: money,
  rounding: money,
  total: money,
  paid: money,
  due: money,
  payments: z.array(
    z.object({
      method: z.string().describe("cash, bkash, nagad, rocket, card, bank…"),
      amount: money,
      reference: z.string().nullable(),
    }),
  ),
});

export const stockAlertObject = z.object({
  object: z.literal("stock_alert"),
  branchId: z.string(),
  variantId: z.string(),
  itemId: z.string(),
  sku: z.string(),
  name: bilingual,
  unit: z.string(),
  onHand: quantity,
  reorderLevel: quantity,
});

export function listOf<T extends z.ZodType>(item: T) {
  return z.object({
    object: z.literal("list"),
    data: z.array(item),
    hasMore: z.boolean(),
    nextCursor: z.string().nullable().describe("Pass as startingAfter for the next page"),
  });
}

export const problemObject = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  code: z.string().describe("Stable machine-readable error code"),
  detail: z.string().optional(),
  requestId: z.string().optional(),
  errors: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
});

/** The body POSTed to webhook endpoints (FR-API-07). */
export const eventObject = z.object({
  id: z.string(),
  object: z.literal("event"),
  type: z.string(),
  apiVersion: z.literal("v1"),
  livemode: z.boolean(),
  createdAt: timestamp,
  data: z.object({ object: z.unknown() }),
});

export type ItemObject = z.infer<typeof itemObject>;
export type CategoryObject = z.infer<typeof categoryObject>;
export type BranchObject = z.infer<typeof branchObject>;
export type StockLevelObject = z.infer<typeof stockLevelObject>;
export type CustomerObject = z.infer<typeof customerObject>;
export type OrderObject = z.infer<typeof orderObject>;
export type SaleObject = z.infer<typeof saleObject>;
export type StockAlertObject = z.infer<typeof stockAlertObject>;
export type EventObject = z.infer<typeof eventObject>;
export interface ListObject<T> {
  object: "list";
  data: T[];
  hasMore: boolean;
  nextCursor: string | null;
}
